import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import tokens from "src/shared/theme/tokens";

// Localised explainer clips that walk the applicant through the ProofUp
// income-verification handoff. Served statically from /public/videos so the
// ~8MB files never enter the JS bundle.
const VIDEO_SOURCES: Record<string, string> = {
  en: "/videos/income-verification-en.mp4",
  es: "/videos/income-verification-es.mp4",
};

// Poster frames (first visual frame of each localised clip) so the player shows
// the video's opening still instead of a black box before playback starts.
const POSTER_SOURCES: Record<string, string> = {
  en: "/videos/income-verification-en.jpg",
  es: "/videos/income-verification-es.jpg",
};

const resolveBase = (language: string | undefined): string =>
  // i18n language can be region-tagged ("es-MX"); match on the base subtag and
  // fall back to English for anything we don't ship a clip for.
  (language || "en").toLowerCase().split("-")[0];

const resolveSource = (language: string | undefined): string => {
  const base = resolveBase(language);
  return VIDEO_SOURCES[base] ?? VIDEO_SOURCES.en;
};

const resolvePoster = (language: string | undefined): string => {
  const base = resolveBase(language);
  return POSTER_SOURCES[base] ?? POSTER_SOURCES.en;
};

/**
 * Explainer video shown above the ProofUp income-verification step, in both the
 * post-payment Lease page and the in-Register widget. Picks the English or
 * Spanish clip from the resident's current i18n language and re-loads when they
 * switch languages. Autoplays muted (so it starts under every browser autoplay
 * policy without blasting audio at the resident); controls stay visible so the
 * resident can unmute, pause, or replay.
 */
export const ProofupHelpVideo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = resolveSource(i18n.language);
  const poster = resolvePoster(i18n.language);

  // The `key` on <video> already remounts it on a language switch, but load()
  // guarantees the new source is picked up, and we (re)attempt the muted
  // autoplay. If a browser still blocks it, the video sits on its poster frame
  // with controls — no error surfaced to the resident.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.load();
    const attempt = el.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {
        /* autoplay blocked by browser policy — resident presses play */
      });
    }
  }, [src]);

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: `${tokens.radius.lg}px`,
        overflow: "hidden",
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: tokens.shadows.card,
        backgroundColor: tokens.colors.surface,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${tokens.colors.border}`,
        }}
      >
        <PlayCircleOutlineIcon
          sx={{ color: tokens.colors.primary, fontSize: 20 }}
        />
        <Typography
          sx={{
            fontFamily: tokens.fonts.display,
            fontWeight: 600,
            fontSize: 15,
            color: tokens.colors.primaryDeep,
          }}
        >
          {t("account.incomeVerification.videoTitle")}
        </Typography>
      </Box>

      <Box
        component="video"
        key={src}
        ref={videoRef}
        controls
        autoPlay
        muted
        playsInline
        preload="metadata"
        poster={poster}
        sx={{
          display: "block",
          width: "100%",
          maxWidth: "100%",
          backgroundColor: "#000",
        }}
      >
        <source src={src} type="video/mp4" />
      </Box>
    </Box>
  );
};

export default ProofupHelpVideo;
